import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Package,
  FolderOpen,
  BarChart3,
  Upload,
  Users,
  ShoppingCart,
  Receipt,
  FileText,
  Settings,
  TrendingUp,
  TrendingDown,
  Boxes,
  ChevronDown,
  UserCog,
  Shield,
  Truck,
  DollarSign,
  ShoppingBag,
  PackageOpen
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useState, useEffect, useRef } from 'react'
import { usePermissions } from '../../hooks/usePermissions'

interface NavItem {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  href: string
  permission?: string
}

interface NavGroup {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  items: NavItem[]
}

const navigationGroups: (NavItem | NavGroup)[] = [
  // Dashboard - standalone
  { icon: Home, label: 'Dashboard', href: '/', permission: 'dashboard.view' },

  // Sales Group
  {
    label: 'Sales',
    icon: DollarSign,
    items: [
      { icon: ShoppingCart, label: 'POS / Billing', href: '/pos', permission: 'sales.create' },
      { icon: Receipt, label: 'Sales Orders', href: '/orders', permission: 'sales.view' },
      { icon: FileText, label: 'Sales Invoices', href: '/invoices', permission: 'invoices.view' },
      { icon: Users, label: 'Customers', href: '/customers', permission: 'customers.view' },
    ]
  },

  // Purchase Group
  {
    label: 'Purchase',
    icon: ShoppingBag,
    items: [
      { icon: ShoppingBag, label: 'Purchase Orders', href: '/purchases', permission: 'purchases.view' },
      { icon: Truck, label: 'Suppliers', href: '/suppliers', permission: 'suppliers.view' },
      { icon: TrendingDown, label: 'Expenses', href: '/expenses', permission: 'expenses.view' },
    ]
  },

  // Inventory Group
  {
    label: 'Inventory',
    icon: PackageOpen,
    items: [
      { icon: Package, label: 'Products', href: '/products', permission: 'products.view' },
      { icon: FolderOpen, label: 'Categories', href: '/categories', permission: 'categories.view' },
      { icon: Boxes, label: 'Units', href: '/units', permission: 'products.view' },
      { icon: BarChart3, label: 'Stock Management', href: '/stock', permission: 'products.manage_stock' },
      { icon: Upload, label: 'Import/Export', href: '/import-export', permission: 'products.view' },
    ]
  },

  // Reports Group
  {
    label: 'Reports',
    icon: TrendingUp,
    items: [
      { icon: TrendingUp, label: 'Sales Reports', href: '/reports', permission: 'sales.view_reports' },
    ]
  },

  // Settings Group
  {
    label: 'Settings',
    icon: Settings,
    items: [
      { icon: UserCog, label: 'Users', href: '/users', permission: 'users.view' },
      { icon: Shield, label: 'Roles & Permissions', href: '/roles', permission: 'roles.view' },
      { icon: Settings, label: 'System Settings', href: '/settings', permission: 'settings.view' },
    ]
  },
]

function isNavGroup(item: NavItem | NavGroup): item is NavGroup {
  return 'items' in item
}

interface HorizontalNavProps {
  className?: string
}

export const HorizontalNav = ({ className }: HorizontalNavProps) => {
  const location = useLocation()
  const { hasPermission } = usePermissions()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const hasVisibleItems = (items: NavItem[]) => {
    return items.some(item => !item.permission || hasPermission(item.permission))
  }

  const visibleNavigation = navigationGroups.filter(item => {
    if (isNavGroup(item)) {
      return hasVisibleItems(item.items)
    }
    return !item.permission || hasPermission(item.permission)
  })

  const handleToggle = (label: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setOpenDropdown(prev => prev === label ? null : label)
  }

  // Close dropdown when clicking anywhere
  useEffect(() => {
    const handleClick = () => setOpenDropdown(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  return (
    <nav className={cn("bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700", className)}>
      <div className="px-4">
        <div className="flex items-center gap-1 h-14 overflow-x-auto">
          {visibleNavigation.map((item, index) => {
            if (isNavGroup(item)) {
              const GroupIcon = item.icon
              const hasActive = item.items.some(navItem => isActive(navItem.href))
              const isOpen = openDropdown === item.label

              return (
                <div
                  key={index}
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Group Button */}
                  <button
                    onClick={(e) => handleToggle(item.label, e)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                      hasActive || isOpen
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    )}
                  >
                    <GroupIcon className="w-4 h-4" />
                    <span>{item.label}</span>
                    <ChevronDown className={cn(
                      "w-3 h-3 transition-transform duration-200",
                      isOpen && "rotate-180"
                    )} />
                  </button>

                  {/* Dropdown Menu */}
                  {isOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[200px] z-50">
                      {item.items
                        .filter(navItem => !navItem.permission || hasPermission(navItem.permission))
                        .map((navItem, itemIndex) => {
                          const ItemIcon = navItem.icon
                          const active = isActive(navItem.href)

                          return (
                            <Link
                              key={itemIndex}
                              to={navItem.href}
                              className={cn(
                                "flex items-center gap-3 px-4 py-2 text-sm transition-colors",
                                active
                                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              )}
                            >
                              <ItemIcon className="w-4 h-4" />
                              <span>{navItem.label}</span>
                            </Link>
                          )
                        })}
                    </div>
                  )}
                </div>
              )
            } else {
              // Standalone item (e.g., Dashboard)
              const ItemIcon = item.icon
              const active = isActive(item.href)

              return (
                <Link
                  key={index}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                    active
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  )}
                >
                  <ItemIcon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              )
            }
          })}
        </div>
      </div>
    </nav>
  )
}
